/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  CodeSnippet,
  CodeSnippetSkeleton,
  FormLabel,
  InlineNotification,
  PasswordInput
} from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUnmount } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '@ibm-watson/shared/ts';
import { useChannelHelpers } from '../../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../../hooks/useFormHelpers';
import { setDraftIntegrationEnableSecretFields } from '../../../../store/channels/actions';
import { selectDraftIntegrationPrivateFieldsEnabled } from '../../../../store/channels/selectors';
import { selectContactCenterConnectionStatus } from '../../../../store/phone/selectors';
import { ChannelContentProps } from '../../../../util/types';
import { sharedMessages } from '../../../messages';
import { subscriptionType } from '../../phone-constants';
import { genesysAudioConnectorURIRegExMatch } from '../../phone-helpers';
import { FIELDS, JWT, StreamProvider } from '../../schema';
import { ContactCenter as messages } from '../messages';

import '../ConnectToContactCenterStep.scss';

export const GenesysAudioConnectorForm = ({
  readOnly
}: ChannelContentProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getConnectURI } = useChannelHelpers(formMethods);

  const apiKeyChangeState = useState<boolean>(false);
  const clientSecretChangeState = useState<boolean>(false);

  const connectionStatus = useAppSelector(selectContactCenterConnectionStatus);

  const { getFieldValue, registerInputField, updateFieldValue } =
    useFormHelpers(formMethods);

  const isSecretFieldsEnabled = useAppSelector(
    selectDraftIntegrationPrivateFieldsEnabled
  );

  const apiKey = getFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.API_KEY);
  const clientSecret = getFieldValue(
    FIELDS.GENESYS_AUDIO_CONNECTOR.CLIENT_SECRET
  );
  const connectURI = getFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.CONNECT_URL);

  useEffect(() => {
    // If no api key and connecturi found, it means we are in initial setup flow so auto generate and
    // set both the apiKey and clientSecret
    // In case of edit flow UI get back connectURI in that case don't automatically generate and
    // set apiKey and clientSecret
    if (!Boolean(apiKey) && !connectURI) {
      updateFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.API_KEY, uuidv4());
      updateFieldValue(
        FIELDS.GENESYS_AUDIO_CONNECTOR.CLIENT_SECRET,
        Buffer.from(uuidv4()).toString('base64')
      );
    }
  }, [apiKey, connectURI, updateFieldValue]);

  useEffect(() => {
    // Need to enable secrets fields for Phone Genesys Audio connector so that user can copy fields easily
    if (!isSecretFieldsEnabled && !connectURI) {
      dispatch(setDraftIntegrationEnableSecretFields(true));
    }
  }, [dispatch, isSecretFieldsEnabled, connectURI]);

  // Once user clicked on secret fields (or isSecretFieldsEnabled), set the required fields
  useEffect(() => {
    if (isSecretFieldsEnabled) {
      updateFieldValue(
        FIELDS.GENESYS_AUDIO_CONNECTOR.PROVIDER_AUTHORIZATION,
        StreamProvider.GENESYS_AUDIO_CONNECTOR
      );
      updateFieldValue(FIELDS.GENESYS_AUDIO_CONNECTOR.AUTHORIZATION_TYPE, JWT);
    }
  }, [isSecretFieldsEnabled, updateFieldValue]);
  useEffect(() => {
    // Only need to generate connect URI once when we auto generate apikey and clientSecrets
    // On Edit flow, if connectURI is getting back, we don't need to update it on apikey or clientSecret change
    if (Boolean(apiKey) && Boolean(clientSecret) && !Boolean(connectURI)) {
      getConnectURI(
        subscriptionType.GENESYS_AUDIO_CONNECTOR,
        apiKey,
        clientSecret
      );
    }
  }, [apiKey, clientSecret, connectURI, getConnectURI]);

  useUnmount(() => {
    // Disable secret fields on unmount of this component
    if (isSecretFieldsEnabled) {
      dispatch(setDraftIntegrationEnableSecretFields(false));
    }
  });

  const subscribeInputData = [
    {
      field: FIELDS.GENESYS_AUDIO_CONNECTOR.API_KEY,
      id: 'api_key_id_input',
      labelText: t(messages.apiKeyLabel),
      placeholder: t(messages.apiKeyPlaceHolder),
      stateChange: apiKeyChangeState
    },
    {
      field: FIELDS.GENESYS_AUDIO_CONNECTOR.CLIENT_SECRET,
      id: 'client_secret_input',
      labelText: t(messages.clientSecretLabel),
      placeholder: t(messages.clientSecretPlaceHolder),
      stateChange: clientSecretChangeState
    }
  ];

  // This renders the Connect URI part based on conditions
  const renderConnectURISection = () => {
    if (
      (connectionStatus === LoaderStates.ACTIVE ||
        connectionStatus === LoaderStates.IDLE) &&
      !connectURI
    ) {
      return <CodeSnippetSkeleton className="genesysAudioConnector__snippet" />;
    } else {
      if (
        (connectionStatus === LoaderStates.ERROR ||
          connectionStatus === LoaderStates.SUCCESS) &&
        !connectURI
      ) {
        return (
          <InlineNotification
            className="genesysAudioConnector__snippet"
            kind="error"
            title={t(messages.connectErrorTitleGenesys)}
            subtitle={t(messages.connectErrorSubtitleGenesys)}
            hideCloseButton={true}
          />
        );
      } else {
        // For business needs, ConnectURI needs to be broken down into two pieces and show in two separate code snippets on the UI
        // 1. Connect URI upto `wss:first part of the URI/genesysaudioconnector`
        // 2. Bot Connector ID {`genesysaudioconnectorId/rest` of the URI}
        const connectURI = getFieldValue(
          FIELDS.GENESYS_AUDIO_CONNECTOR.CONNECT_URL
        );

        // This essesntially gives back an array with entire URI as first element,
        // baseURI upto /genesysaudioconnector part as a second element
        // thried element is the rest of the URI.
        const [, baseURI, botConnectorID] = connectURI?.match(
          genesysAudioConnectorURIRegExMatch
        ) ?? [, '', ''];

        const connectURISnippet = [
          {
            id: 'baseURI',
            label: messages.generatedConnectURILabel,
            uriPart: baseURI
          },
          {
            id: 'botId',
            label: messages.botConnectorIdLabel,
            uriPart: botConnectorID
          }
        ];

        return connectURISnippet.map(({ id, label, uriPart }, index) => (
          <div
            className="TabContent__field-container ConnectToContactCenterStep__connect-snippet-container"
            key={`${label}_${index}`}>
            <FormLabel className="TabContent__field-label">
              <FormattedMessage {...label} />
            </FormLabel>
            <CodeSnippet
              id={`genesys-audio-connector-generated-connect-url-${id}`}
              className="genesysAudioConnector__snippet"
              aria-label={t(label)}
              feedback={t(sharedMessages.copied)}
              copyButtonDescription={t(sharedMessages.copyToClipboard)}
              copyLabel={t(sharedMessages.copyToClipboard)}
              onClick={() => copyContentToClipboard(uriPart)}
              type="single">
              {uriPart}
            </CodeSnippet>
          </div>
        ));
      }
    }
  };

  // Main JSX return
  return (
    <>
      <div className="ConnectToContactCenterStep__title">
        {t(messages.connectToContactCredentials)}
      </div>
      <div className="ConnectToContactCenterStep__instructions TabContent__spacer16">
        <strong>{t(messages.important)}</strong>
        {t(messages.genesysAudioConnectorInstructions)}
      </div>
      <div className="TabContent__spacer8">
        {subscribeInputData.map((inputData, index) => (
          <div
            className="TabContent__field-container ConnectToContactCenterStep__connect-input-container"
            key={`text-input-${index}-${inputData.id}`}>
            {
              <PasswordInput
                {...registerInputField({
                  field: inputData.field,
                  isPasswordField: true,
                  placeholderText: inputData.placeholder,
                  changeState: inputData.stateChange,
                  readOnly
                })}
                id={inputData.id}
                labelText={
                  <div className="ConnectToContactCenterStep__input-label">
                    {inputData.labelText}
                  </div>
                }
                data-toggle-password-visibility={
                  inputData.id === 'api_key_id_input'
                }
              />
            }
          </div>
        ))}
        {renderConnectURISection()}
      </div>
    </>
  );
};
